/* eslint no-underscore-dangle: 0 */
import saveAs from 'file-saver';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PSPDFKitWeb from 'pspdfkit';
import { connect } from 'react-redux';
import './pspdfkit.css';
import OfflineFileStore from 'offline/util/OfflineFileStore';
import OfflineAnnotationStore from 'offline/util/OfflineAnnotationStore';
import { addOfflineWaitingForSyncId } from 'offline/store/actions';
import downloadIcon from 'assets/images/icons/download.svg';

class PdfViewer extends Component {
  constructor(props, context) {
    super(props, context);
    this._instance = null;
    this._container = null;

    this.onRef = this.onRef.bind(this);
    this.load = this.load.bind(this);
    this.unload = this.unload.bind(this);
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (this.props.documentId !== prevProps.documentId) {
      this.unload();
      this.load(this.props);
    }
  }

  componentWillUnmount() {
    this.unload();
  }

  onRef(container) {
    this._container = container;
  }

  downloadPdf = () => {
    this._instance.exportPDF()
      .then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/pdf' });
        saveAs(blob, this.props.fileName);
      });
  };

  isUsingPsPdfKitServer = () => this.props.pdf === null;

  async isOfflineDocument() {
    return OfflineFileStore.keys().then((keys) => keys.includes(this.props.documentId.toString()));
  }

  unload() {
    PSPDFKitWeb.unload(this._instance || this._container);
    this._instance = null;
  }

  async load() {
    const { addOfflineWaitingForSyncId, documentId, onDownloadOriginal } = this.props;
    const documentConfig = this.isUsingPsPdfKitServer()
      ? {
        serverUrl: process.env.REACT_APP_PSPDFKIT_SERVER_URL,
        authPayload: { jwt: this.props.psPdfKitAccessToken },
        documentId: this.props.documentId,
        autoSaveMode: PSPDFKitWeb.AutoSaveMode.IMMEDIATE,
      }
      : {
        pdf: this.props.pdf,
        instantJSON: this.props.instantJson,
        container: this._container,
        licenseKey: process.env.REACT_APP_PSPDFKIT_LICENSE_KEY,
      };

    const toolbarItems = [
      { type: 'sidebar-thumbnails', selected: true },
      { type: 'sidebar-document-outline' },
      { type: 'sidebar-annotations' },
      { type: 'sidebar-bookmarks' },
      { type: 'pager' },
      { type: 'pan' },
      { type: 'zoom-out' },
      { type: 'zoom-in' },
      { type: 'zoom-mode' },
      { type: 'spacer' },
      { type: 'annotate' },
      { type: 'ink' },
      { type: 'ink-eraser' },
      { type: 'highlighter' },
      // { type: 'ink-signature' },
      // { type: 'image' },
      // { type: 'stamp' },
      { type: 'note' },
      { type: 'text' },
      // { type: 'line' },
      // { type: 'arrow' },
      // { type: 'rectangle' },
      // { type: 'ellipse' },
      // { type: 'polygon' },
      // { type: 'polyline' },
      { type: 'search' },
      { type: 'debug' },
      {
        type: 'responsive-group',
        id: 'export',
        icon: 'downloadIcon',
        mediaQueries: ['(max-width: 992px)'],
      },
      {
        type: 'print',
        responsiveGroup: 'export',
      },
      {
        type: 'custom',
        title: 'PDF with annotations',
        id: 'download',
        icon: downloadIcon,
        dropdownGroup: 'download',
        responsiveGroup: 'export',
        onPress: this.downloadPdf,
      },
    ];

    if (onDownloadOriginal) {
      toolbarItems.push({
        type: 'custom',
        title: 'Original',
        id: 'download-original',
        icon: downloadIcon,
        dropdownGroup: 'download',
        responsiveGroup: 'export',
        onPress: onDownloadOriginal,
      });
    }

    const initialViewState = new PSPDFKitWeb.ViewState({
      showToolbar: true,
      enableAnnotationToolbar: true,
      sidebarMode: window.innerWidth > 1024 ? PSPDFKitWeb.SidebarMode.THUMBNAILS : null,
    });

    this._instance = await PSPDFKitWeb.load({
      ...documentConfig,
      initialViewState,
      enableServiceWorkerSupport: true,
      instant: false,
      disableWebAssemblyStreaming: true,
      container: this._container,
      styleSheets: [
        '/pspdfkit.css',
      ],
      baseUrl: `${window.location.origin}/`,
      toolbarItems,
    });

    const isOfflineDocument = await this.isOfflineDocument();

    // Save annotation changes in offline store
    if (isOfflineDocument) {
      if (this.isUsingPsPdfKitServer()) {
        this._instance.addEventListener('annotations.didSave', () => {
          this._instance.exportInstantJSON()
            .then((instantJson) => {
              OfflineAnnotationStore.set(this.props.documentId, instantJson);
            });
        });
      } else {
        this._instance.addEventListener('annotations.update', () => {
          addOfflineWaitingForSyncId(documentId);
          this._instance.exportInstantJSON()
            .then((instantJson) => OfflineAnnotationStore.set(this.props.documentId, instantJson));
        });
        this._instance.addEventListener('annotations.create', () => {
          addOfflineWaitingForSyncId(documentId);
          this._instance.exportInstantJSON()
            .then((instantJson) => OfflineAnnotationStore.set(this.props.documentId, instantJson));
        });
        this._instance.addEventListener('annotations.delete', () => {
          addOfflineWaitingForSyncId(documentId);
          this._instance.exportInstantJSON()
            .then((instantJson) => OfflineAnnotationStore.set(this.props.documentId, instantJson));
        });
      }
    }
  }

  render() {
    const { style } = this.props;
    return (
      <div
        ref={this.onRef}
        style={{ width: '100%', height: '100%', ...style, zIndex: '1 !important', position: 'relative' }}
      />
    );
  }
}
PdfViewer.propTypes = {
  pdf: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  documentId: PropTypes.number.isRequired,
  psPdfKitAccessToken: PropTypes.string,
  instantJson: PropTypes.objectOf(PropTypes.any),
  addOfflineWaitingForSyncId: PropTypes.func.isRequired,
  style: PropTypes.objectOf(PropTypes.any),
  onDownloadOriginal: PropTypes.func,
  fileName: PropTypes.string,
};
PdfViewer.defaultProps = {
  psPdfKitAccessToken: '',
  pdf: null,
  instantJson: null,
  style: {},
  onDownloadOriginal: null,
  fileName: 'download',
};

const mapDispatchToProps = {
  addOfflineWaitingForSyncId,
};
export default connect(
  null,
  mapDispatchToProps,
  null,
  { forwardRef: true },
)(PdfViewer);
